import React from "react"
import styled from "styled-components"
import {connect} from "@irati/store"
import {isNewRecognition} from "@irati/recognition/utils/polls/isNew"
import FormattedMessage, {FormattedRelative, injectIntl, InjectIntl} from "@irati/utils/lang"
import mainTheme from "@irati/ui/src/global/theme"
import {GroupCelebration, GroupCelebrationStatus} from "@irati/types"
import momentTZ from "moment-timezone"
import Flex from "@irati/ui/src/Flex"
import Icon, {IconTypes} from "@irati/ui/src/Icon"
import usersListToString from "@irati/recognition/utils/usersListToString"
import {Body} from "@irati/ui/src/Typography"
import Spacing from "@irati/ui/src/Spacing"
import authentication from "@irati/auth"
import {useTheme} from "@irati/ui/src/global/useTheme"
import {RouterLink} from "../../shared"
import {FeedItem} from "../../Feed"
import ProfilePicGroup, {OUTER_WRAPPER_SIZE, SINGLE_PICTURE_SIZE} from "../../ProfilePicGroup"
import {ActivityScheduledGroupCelebration as IconsActivity} from "../../Icons"
import BodyWrapper from "../../shared/BodyWrapper"
import Comment from "../../Popup/shared/Comments/Comment"
import {getGroupCelebrationPath} from "../../../utils/paths"
import {FlexRow, BodyContainer, ProfilePicContainer, CelebrationIcon} from "./styled"
import {GroupCelebration as ScheduledGroupCelebrationTitle} from "./Titles"
import {EndDate} from "./styled/Card"
import Confetti from "./Confetti"

const MAX_CHARACTERS_MESSAGE = 140
const AVATAR_SIZE = 30
const MAX_NUM_OF_AVATARS = 5
const DEFAULT_AVATAR_BORDER = 3
const MAX_NUM_OF_MESSAGES = 2
interface ScheduledGroupCelebrationProps extends InjectIntl {
  groupCelebration: GroupCelebration
  className: string
  isMobile: boolean
  id: string
}
const GroupCelebrationFeedItem = styled(FeedItem)`
  background-image: ${({image}: {image: string}) => `url(${image})`};
  background-repeat: repeat-x;
  background-size: 500px;
  background-position: top;
`
const getOuterSize = (total: number) => {
  return AVATAR_SIZE + ((AVATAR_SIZE / 2 - DEFAULT_AVATAR_BORDER) * total - 1)
}
const ScheduledGroupCelebration: React.FC<ScheduledGroupCelebrationProps> = (props) => {
  const {groupCelebration, className, formatMessage, isMobile, id: feedItemId = "scheduled-group-celebration"} = props
  const timezone = groupCelebration.originalTimezone
  const theme = useTheme()
  const failed = groupCelebration.status === GroupCelebrationStatus.PublishFailure
  const scheduledDate = momentTZ.tz(groupCelebration.when, timezone).format("Do, MMMM YYYY, h:mm A")
  const dateMessage = groupCelebration.rewardAmount
    ? "platform.recognition.scheduledReward.date.lbl"
    : "platform.recognition.scheduled.date.lbl"
  const userComment = groupCelebration.messages?.find((item) => item.createdBy?.uuid === authentication.user().uuid)
  const commentToPreview = userComment || groupCelebration.messages?.[0]
  const usersAvatar = [groupCelebration.author, ...groupCelebration.collaborators!]
  const isNew = groupCelebration.status === GroupCelebrationStatus.Active && isNewRecognition(groupCelebration)
  const isReceiver =
    groupCelebration.status === GroupCelebrationStatus.Active && groupCelebration.receiver.uuid === authentication.user().uuid
  return (
    <RouterLink scrollToTop={false} plain to={getGroupCelebrationPath(groupCelebration)}>
      <GroupCelebrationFeedItem className={className} image={groupCelebration.greeting?.image?.href!} isNew={isNew}>
        {isNew && isReceiver && <Confetti />}
        <Spacing marginTop={6}>
          <FlexRow>
            <ProfilePicContainer>
              <CelebrationIcon src={groupCelebration?.greeting?.icon?.href} />
              <ProfilePicGroup
                users={[groupCelebration?.receiver!]}
                outerWrapperSize={OUTER_WRAPPER_SIZE(isMobile)}
                singlePictureSize={SINGLE_PICTURE_SIZE(isMobile)}
              />
            </ProfilePicContainer>
            <BodyWrapper>
              <BodyContainer>
                <ScheduledGroupCelebrationTitle groupCelebration={groupCelebration} />
                {!failed && (
                  <EndDate>
                    {groupCelebration.status === GroupCelebrationStatus.Scheduled && (
                      <FormattedMessage id={dateMessage} values={{date: scheduledDate, timezone}} />
                    )}
                    {groupCelebration.status === GroupCelebrationStatus.Active && <FormattedRelative value={groupCelebration.when} />}
                  </EndDate>
                )}
                {failed && (
                  <Flex color={mainTheme.colors.error} alignItems="center" gap={1}>
                    <Icon icon={IconTypes.circleAlertFill} />
                    <Body small color={mainTheme.colors.error}>
                      <FormattedMessage id="platform.recognition.scheduled.publishFailed" values={{date: scheduledDate}} />
                    </Body>
                  </Flex>
                )}
                <Flex marginTop={1} gap={1} alignItems="center">
                  <ProfilePicGroup
                    horizontal
                    maxNumberOfUsers={MAX_NUM_OF_AVATARS}
                    users={usersAvatar}
                    outerWrapperSize={getOuterSize(usersAvatar.length > MAX_NUM_OF_AVATARS ? MAX_NUM_OF_AVATARS : usersAvatar.length)}
                    multiplePictureSize={AVATAR_SIZE}
                    singlePictureSize={AVATAR_SIZE}
                  />
                  <Body small marginTop={theme.spacing.half}>
                    {usersListToString(usersAvatar, formatMessage)}
                  </Body>
                </Flex>
                {groupCelebration.status === GroupCelebrationStatus.Scheduled && commentToPreview && (
                  <Spacing marginTop={2}>
                    <Comment comment={commentToPreview} hideActions hideTime maxCharacters={MAX_CHARACTERS_MESSAGE} />
                  </Spacing>
                )}
                {groupCelebration.status === GroupCelebrationStatus.Active && (
                  <Flex direction="column" marginTop={2} gap={2}>
                    {groupCelebration.messages?.slice(0, MAX_NUM_OF_MESSAGES).map((message) => (
                      <Comment key={message.uuid} comment={message} hideActions hideTime maxCharacters={MAX_CHARACTERS_MESSAGE} />
                    ))}
                  </Flex>
                )}
                <Spacing marginTop={3}>
                  <IconsActivity feedItemId={feedItemId} item={groupCelebration} />
                </Spacing>
              </BodyContainer>
            </BodyWrapper>
          </FlexRow>
        </Spacing>
      </GroupCelebrationFeedItem>
    </RouterLink>
  )
}

const mapStateToProps = ({mq}) => {
  return {
    isMobile: mq.lessThan.md
  }
}
export default connect(mapStateToProps)(injectIntl(ScheduledGroupCelebration))
